import type { Attribute } from "@tiptap/core"
import * as z from "zod"

export type TiptapAttributeOverrides<Shape extends z.ZodRawShape> = Partial<{
  [K in keyof Shape]: Partial<Attribute> & {
    dataAttr?: string
  }
}>

function getBaseType(schema: z.ZodType): z.ZodType {
  let cur = schema
  while ((cur as any)._def?.innerType) {
    cur = (cur as any)._def.innerType
  }
  return cur
}

/**
 * Derives Tiptap attribute definitions (defaults and renderHTML/parseHTML functions)
 * from a Zod object schema with one entry per attribute.
 *
 * Each attribute is rendered to HTML as a kebab-case data-* attribute.
 * Booleans use presence, while arrays/objects use JSON string values.
 *
 * Override native attributes such as href/width and legacy data-* names at the
 * extension: changing a field name otherwise changes the HTML contract too.
 */
export function toTiptapAttributes<Shape extends z.ZodRawShape>(
  schema: z.ZodObject<Shape>,
  overrides?: TiptapAttributeOverrides<Shape>,
): Record<keyof Shape, Attribute> {
  const result = {} as Record<keyof Shape, Attribute>

  for (const [key, field] of Object.entries(schema.shape)) {
    const override = overrides?.[key as keyof Shape]
    const dataName = override?.dataAttr ?? key.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase()
    const dataAttr = `data-${dataName}`
    const altDataAttr = `data-${key.toLowerCase()}`

    const fieldSchema = field as z.ZodType
    const defaultParsed = fieldSchema.safeParse(undefined)
    const defaultValue = defaultParsed.success ? defaultParsed.data : null

    const baseType = getBaseType(fieldSchema)
    const isBool = baseType instanceof z.ZodBoolean
    const isArray = baseType instanceof z.ZodArray
    const isObject = baseType instanceof z.ZodObject || baseType instanceof z.ZodRecord
    const isNumber = baseType instanceof z.ZodNumber
    const isRequired = !defaultParsed.success

    let baseAttr: Attribute

    if (isBool) {
      baseAttr = {
        default: defaultValue ?? false,
        parseHTML: (el) => el.hasAttribute(dataAttr) || el.hasAttribute(altDataAttr),
        renderHTML: (attrs) => (attrs[key] ? { [dataAttr]: "" } : {}),
      }
    } else if (isArray || isObject) {
      baseAttr = {
        default: defaultValue,
        parseHTML: (el) => {
          const raw = el.getAttribute(dataAttr) ?? el.getAttribute(altDataAttr)
          if (!raw) return defaultValue
          try {
            return JSON.parse(raw)
          } catch {
            return defaultValue
          }
        },
        renderHTML: (attrs) =>
          attrs[key] !== undefined && attrs[key] !== null
            ? { [dataAttr]: JSON.stringify(attrs[key]) }
            : {},
      }
    } else if (isNumber) {
      baseAttr = {
        default: defaultValue,
        parseHTML: (el) => {
          const raw = el.getAttribute(dataAttr) ?? el.getAttribute(altDataAttr)
          if (raw === null) return defaultValue
          const num = Number(raw)
          return Number.isNaN(num) ? defaultValue : num
        },
        renderHTML: (attrs) =>
          attrs[key] !== undefined && attrs[key] !== null ? { [dataAttr]: String(attrs[key]) } : {},
      }
    } else {
      baseAttr = {
        default: defaultValue,
        parseHTML: (el) =>
          el.getAttribute(dataAttr) ?? el.getAttribute(altDataAttr) ?? defaultValue,
        renderHTML: (attrs) =>
          attrs[key] !== undefined && attrs[key] !== null ? { [dataAttr]: String(attrs[key]) } : {},
      }
    }

    if (isRequired) {
      baseAttr.isRequired = true
    }

    const { dataAttr: _, ...attrOverrides } = override ?? {}
    result[key as keyof Shape] = {
      ...baseAttr,
      ...attrOverrides,
    }
  }

  return result
}
